// react
import { forwardRef } from 'react';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';

// material ui
import { alpha, CircularProgress } from '@mui/material';
import Button, { type ButtonProps } from '@mui/material/Button';

export interface AddButtonProps extends Omit<ButtonProps, 'variant' | 'color'> {
  /** Shows a spinner in place of `startIcon` and disables the button. */
  loading?: boolean;
}

/**
 * Reusable primary "add" action button.
 *
 * Shares the exact look of `CreateButtonLink` (same green palette, hover and
 * disabled states) but always renders a leading `AddRoundedIcon` and does
 * not navigate anywhere - use this for actions like opening a dialog or
 * adding an item to a list in place.
 */
export const AddButton = forwardRef<HTMLButtonElement, AddButtonProps>(
  (
    { loading = false, disabled, startIcon = <AddRoundedIcon />, sx, ...rest },
    ref,
  ) => (
    <Button
      ref={ref}
      variant="contained"
      disabled={disabled || loading}
      startIcon={
        loading ? <CircularProgress size={16} color="inherit" /> : startIcon
      }
      sx={[
        (theme) => ({
          color: '#ffffff',
          border:
            theme.palette.mode === 'dark'
              ? '1px solid #3fb950'
              : '1px solid #1b1f2326',
          backgroundColor:
            theme.palette.mode === 'dark' ? '#238636' : '#2ea44f',
          borderColor: theme.palette.mode === 'dark' ? '#3fb950' : '#1b1f2326',
          boxShadow:
            theme.palette.mode === 'dark'
              ? 'rgba(1, 4, 9, 0.35) 0px 1px 0px 0px, rgba(255, 255, 255, 0.06) 0px 1px 0px 0px inset'
              : 'rgba(27, 31, 35, 0.04) 0px 1px 0px 0px, rgba(255, 255, 255, 0.25) 0px 1px 0px 0px inset',
          transition: '0.2s cubic-bezier(0.3, 0, 0.5, 1)',
          transitionProperty: 'color, background-color, border-color',
          '&:hover': {
            color: '#ffffff',
            backgroundColor:
              theme.palette.mode === 'dark' ? '#2ea043' : '#2c974b',
            borderColor:
              theme.palette.mode === 'dark' ? '#3fb950' : '#1b1f2326',
            transitionDuration: '0.1s',
          },
          '&.Mui-disabled': {
            color: alpha('#ffffff', 0.7),
            borderColor:
              theme.palette.mode === 'dark'
                ? alpha('#3fb950', 0.35)
                : alpha('#1b1f2326', 0.34),
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha('#238636', 0.45)
                : alpha('#2ea44f', 0.45),
            boxShadow: 'none',
          },
        }),
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
      {...rest}
    />
  ),
);

AddButton.displayName = 'AddButton';
